/*
 * AssetFanout — "one project, every asset", as a fan-out.
 *
 * Inputs converge on ONE PROJECT; the project fans out to everything a
 * single run can produce. Every label is product vocabulary:
 *   inputs      -> NewProject (screenshots, voice note / typed notes,
 *                  screen recording + the optional browser extension)
 *   video       -> backend/models.py aspect_ratio ("16:9" | "9:16" | "1:1")
 *   captions    -> .srt / .vtt sidecars, or burnt in
 *   dubs        -> backend/sarvam.py catalog: Indian languages + en-IN
 *   versions    -> backend/models.py Project.versions (restore saves the
 *                  current render first)
 * No counts, no percentages: this graphic makes no quantitative claim.
 *
 * Structure: hierarchy (many -> one -> many). All text is real, in two
 * lists; the wiring is CSS rules with aria-hidden. One DOM tree serves
 * both layouts: three columns joined by bus lines from `lg` up, a
 * vertical stack joined by down-arrows below — nothing is duplicated.
 *
 * Wiring geometry (lg+): the column gap is 3.5rem, so every stub is
 * half of it (1.75rem) plus the list's 1px border, and each bus line
 * sits on the gap's centre. Each <li> draws its own bus segment (top
 * half, full, bottom half), so rows of any height still join up.
 * Inputs are ink; outputs are red — but direction is also carried by
 * arrowheads and the "goes in / comes out" labels, never colour alone.
 */
import { Image, Mic, MonitorPlay, Film, Captions, FileText, Sparkles, Languages, History } from "lucide-react";

const INPUTS = [
  { icon: Image,       label: "Screenshots",               detail: "Arranged into scenes." },
  { icon: Mic,         label: "Voice note or typed notes", detail: "Structured into a script." },
  { icon: MonitorPlay, label: "Screen recording",          detail: "The optional browser extension adds clicks and key presses." },
];

const OUTPUTS = [
  { icon: Film,      label: "Video",              detail: "MP4 in 16:9, 9:16 or 1:1." },
  { icon: Captions,  label: "Captions",           detail: ".srt and .vtt, or burnt in." },
  { icon: FileText,  label: "PDF product manual", detail: "Written documentation to match the video." },
  { icon: Sparkles,  label: "Launch kit",         detail: "Product Hunt copy, LinkedIn posts, email, ad scripts, SEO meta." },
  { icon: Languages, label: "Dubs",               detail: "Indian languages and English (India), with native voices." },
  { icon: History,   label: "Versions",           detail: "Every regeneration is kept and can be restored." },
];

/* Bus segment for row i of n: first row runs from its middle down,
   last row from its top to its middle, the rest full height. The
   -bottom-px bridges the 1px rule between rows. */
const busSpan = (i, n) =>
  i === 0 ? "top-1/2 -bottom-px" : i === n - 1 ? "top-0 bottom-1/2" : "top-0 -bottom-px";

/* A right-pointing arrowhead, drawn at the end of a stub. */
function ArrowHead({ color, className }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="7"
      height="10"
      viewBox="0 0 7 10"
      className={`hidden lg:block absolute top-1/2 -translate-y-1/2 ${className}`}
    >
      <polygon points="0,0 7,5 0,10" fill={color} />
    </svg>
  );
}

/* Below `lg` the columns stack; a down-arrow joins each stage. */
function DownArrow({ color }) {
  return (
    <div aria-hidden="true" className="lg:hidden flex justify-center py-3">
      <svg width="12" height="36" viewBox="0 0 12 36" focusable="false">
        <line x1="6" y1="0" x2="6" y2="26" stroke={color} strokeWidth="2" />
        <polygon points="0,24 12,24 6,36" fill={color} />
      </svg>
    </div>
  );
}

function Item({ icon: Icon, label, detail, accent }) {
  return (
    <>
      <span
        aria-hidden="true"
        className="shrink-0 grid place-items-center w-9 h-9 border border-rule bg-surface"
      >
        <Icon size={18} strokeWidth={2} className={accent ? "text-ink-accent" : "text-ink"} />
      </span>
      <div className="min-w-0">
        <p className="font-display font-black text-base tracking-tight">{label}</p>
        <p className="font-mono text-xs text-ink-muted mt-0.5">{detail}</p>
      </div>
    </>
  );
}

export default function AssetFanout() {
  return (
    <figure className="m-0 border border-rule bg-canvas">
      {/* The one-sentence text equivalent — visible, so it serves
          sighted readers skimming the page as well. */}
      <figcaption className="px-5 py-4 md:px-8 border-b border-rule">
        <span className="eyebrow">One project, every asset</span>
        <p className="font-mono text-sm text-ink-muted mt-1 max-w-prose">
          Screenshots, a voice note or typed notes, or a screen recording go
          into one DemoCraft project, which produces a video, captions, a PDF
          product manual, a launch kit, dubs and a history of versions.
        </p>
      </figcaption>

      {/* Three grid cells at lg. The inputs and the project are centred
          on the row the outputs define, so the shape itself reads as a
          funnel: a short stack in, one block, a tall stack out. Both
          buses pass through the row's midpoint, where the trunks join.
          Input rows are equal height at lg (auto-rows-fr), so a label
          that wraps in a narrow column doesn't make one row lumpy. */}
      <div className="p-5 md:p-8 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,13rem)_minmax(0,1.45fr)] lg:gap-x-14">
        {/* ---------------- inputs ---------------- */}
        <div className="lg:self-center">
          <p id="af-in" className="eyebrow mb-3">What goes in</p>
          <ul
            aria-labelledby="af-in"
            className="grid gap-px bg-rule border border-rule sm:grid-cols-3 lg:grid-cols-1 lg:auto-rows-fr"
          >
            {INPUTS.map((it, i) => (
              <li key={it.label} className="relative bg-canvas flex items-start lg:items-center gap-3 p-4">
                <span aria-hidden="true" className="hidden lg:block absolute top-1/2 left-full w-[calc(1.75rem+1px)] h-px bg-rule" />
                <span aria-hidden="true" className={`hidden lg:block absolute left-[calc(100%+1px+1.75rem)] w-px bg-rule ${busSpan(i, INPUTS.length)}`} />
                <Item {...it} />
              </li>
            ))}
          </ul>
        </div>

        <DownArrow color="var(--border)" />

        {/* ---------------- the project ---------------- */}
        <div className="relative max-w-xs mx-auto w-full lg:max-w-none lg:self-center">
          {/* trunk in (ink) and trunk out (red), each half the gap */}
          <span aria-hidden="true" className="hidden lg:block absolute top-1/2 right-full w-7 h-px bg-rule" />
          <ArrowHead color="var(--border)" className="right-full" />
          <span aria-hidden="true" className="hidden lg:block absolute top-1/2 left-full w-7 h-0.5 -translate-y-1/2 bg-brand" />

          <div className="brutal-card brutal-card-ink shadow-brutal-accent text-center">
            <div aria-hidden="true" className="flex justify-center gap-1.5 mb-4">
              <span className="w-2.5 h-2.5 bg-brand" />
              <span className="w-2.5 h-2.5 bg-state-warning" />
              <span className="w-2.5 h-2.5 bg-state-success" />
            </div>
            <p className="font-display font-black type-heading tracking-tighter">
              ONE<br />PROJECT
            </p>
            <p className="font-mono text-2xs tracking-widest uppercase mt-3">
              Script · scenes · narration
            </p>
          </div>
        </div>

        <DownArrow color="var(--accent)" />

        {/* ---------------- outputs ---------------- */}
        <div>
          <p id="af-out" className="eyebrow mb-3">What comes out</p>
          <ul
            aria-labelledby="af-out"
            className="grid gap-px bg-rule border border-rule sm:grid-cols-2 lg:grid-cols-1"
          >
            {OUTPUTS.map((it, i) => (
              <li key={it.label} className="relative bg-canvas flex items-start gap-3 p-4">
                <span aria-hidden="true" className="hidden lg:block absolute top-1/2 right-full w-[calc(1.75rem+1px)] h-0.5 -translate-y-1/2 bg-brand" />
                <span aria-hidden="true" className={`hidden lg:block absolute right-[calc(100%+1px+1.75rem)] w-0.5 bg-brand ${busSpan(i, OUTPUTS.length)}`} />
                <ArrowHead color="var(--accent)" className="right-[calc(100%+1px)]" />
                <Item {...it} accent />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </figure>
  );
}
